import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function GloucestershireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Gloucestershire Pothole Claims | GREEN Rated, 48,000 Repairs | Fixtyer"
        description="Gloucestershire County Council earns GREEN DfT ratings but filled nearly 48,000 potholes in 2024/25 and reports 23.7% of U-roads in RED condition. See what that means for your Section 58 claim."
        canonical="/councils/gloucestershire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Gloucestershire', url: '/councils/gloucestershire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Does Gloucestershire\'s GREEN rating mean I cannot claim?',
            answer: 'No. GREEN means the council performs above average on network-level DfT scorecards — not that every road is defect-free. Section 58 under the Highways Act 1980 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Gloucestershire\'s own data shows 62,131 safety defects recorded in 2024/25 and 23.7% of unclassified roads in RED condition.',
          },
          {
            question: 'Why is Best Practice AMBER if everything else is GREEN?',
            answer: 'The DfT scores condition, spend and best practice separately before combining them into an overall rating. Gloucestershire is GREEN on condition and spend but AMBER on best practice — the one scorecard that measures documented processes, asset management evidence and data submission. In a dispute, that is the official assessment that the council\'s systems have documented room for improvement, even while the network headline looks strong.',
          },
          {
            question: 'What if my pothole was on an unclassified road?',
            answer: 'Unclassified roads make up 3,283km — 56% of Gloucestershire\'s 5,868km network. Only 33% of U-roads are surveyed annually (800km per year on a three-year cycle). RED-condition U-roads have risen from 12% in 2020 to 23.7% in 2024 under the council\'s Vaisala Road-AI system — roughly 778km of residential and rural routes. The council itself notes these RED figures "were not created using a nationally recognised or comparable data standard" and that pre-2022 figures are not directly comparable.',
          },
          {
            question: 'Does the council spending more than its DfT allocation weaken my claim?',
            answer: 'It strengthens their Section 58 defence on aggregate — Gloucestershire spent £45.7m capital against £25.1m DfT allocation in 2024/25. But spend scorecards measure budgets, not your specific defect. The council still recorded 47,981 pothole repairs and 62,131 safety defects in 2024/25, with 42% of maintenance spend classed as reactive. A well-funded council can still miss the pothole that damaged your car.',
          },
          {
            question: 'How does Section 41 of the Highways Act 1980 apply here?',
            answer: 'Section 41 imposes a duty on Gloucestershire County Council, as highway authority, to maintain adopted highways. Section 58 provides a defence if the council can prove it took such care as was reasonable to secure that the part of the highway in question was not dangerous. Your claim succeeds when you show the defect was dangerous, caused your damage, and the council either knew or ought to have known about it through its inspection regime — regardless of GREEN network ratings.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Gloucestershire County Council"
        councilSlug="gloucestershire"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/gloucestershire"
        headline="Gloucestershire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Gloucestershire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Gloucestershire: Green-Rated, Still Filling 48,000 Potholes a Year
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Gloucestershire County Council earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards
            for condition and spend on a 5,868km network — yet its own transparency report records{' '}
            <strong className="text-rose-400">47,981 pothole repairs in 2024/25</strong>,{' '}
            <strong className="text-rose-400">23.7% of U-roads in RED condition</strong>, and an{' '}
            <strong className="text-amber-400">AMBER best-practice scorecard</strong>. GREEN is a network average.
            Your claim turns on the specific defect.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">47,981</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Part of 62,131 recorded safety defects — roughly 131 pothole repairs every day across
              Gloucestershire, despite three GREEN DfT scorecards.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What GREEN Actually Means Here
          </h2>
          <p className="text-surface-600 mb-8">
            Three GREEN scorecards and one AMBER — the honest read before you claim
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Gloucestershire County Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">What it measures</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">Combined network performance vs other authorities</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">SCANNER survey results on classified roads</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">Investment relative to DfT allocation and network need</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">Asset management evidence, data submission, documented processes</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-emerald-800 text-sm">
                <strong>What GREEN means:</strong> Gloucestershire performs above average compared to other
                councils — many of whom are failing. Classified A-roads are 86% green-condition; B and C roads
                sit at 71% green. The council spends well beyond its DfT capital allocation and publishes a
                detailed Transport Asset Management Plan updated in April 2025.
              </p>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 text-sm">
                <strong>Context for your claim:</strong> You are dealing with a competent authority, not
                systematic neglect. Section 58 will be well-documented. But GREEN does not mean zero defects —
                the council recorded 200,996 pothole repairs over five years and admits U-road condition data
                uses a non-comparable methodology. Your claim needs the specific defect, not a generic
                accusation of failure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Scale of Gloucestershire's Network
          </h2>
          <p className="text-surface-600 mb-8">
            5,868km of adopted roads — more than half unclassified
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of carriageway</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">594.9km</td>
                  <td className="px-6 py-4 text-center">10.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">1,984.3km</td>
                  <td className="px-6 py-4 text-center">33.8%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">3,283.2km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">56.0%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">5,868.4km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">3,729km</div>
              <div className="text-sm text-surface-600 mt-1">Footways maintained</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">5,645km</div>
              <div className="text-sm text-surface-600 mt-1">Public rights of way</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">426km</div>
              <div className="text-sm text-surface-600 mt-1">Cycleways</div>
            </div>
          </div>

          <p className="text-surface-600 text-sm">
            Gloucestershire does not maintain 228km of trunk roads and motorways — those fall to National
            Highways. For pothole damage claims on adopted county roads, Gloucestershire County Council is
            the highway authority under Section 41 of the Highways Act 1980.
          </p>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER data on classified roads — and a worsening U-road picture
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (594.9km): genuinely strong</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 2% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">13%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 17% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">86%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 81% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              50% of A roads surveyed in both directions, annually. Credit where due: main roads have improved.
              Claims on A-roads face the strongest Section 58 defence.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (1,984.3km): stable but a third in amber</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              50% of B and C roads surveyed in both directions, annually. RED has ticked up from 3% to 4%
              over five years; 29% of the B/C network is in RED or amber — maintenance may be required soon
              on roughly 575km.
            </p>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">U roads (3,283.2km): RED condition nearly doubled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">14.5%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">18.2%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23.7%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 text-sm mb-4">
              At 23.7%, roughly <strong>778km</strong> of Gloucestershire's unclassified network is in RED
              condition — village lanes, estate roads and rural routes where most residents drive daily.
              33% of U-roads are surveyed in a single direction, annually.
            </p>
            <QuoteCard
              quote={'The "red" values indicated in the above U road table are generated using advice from system suppliers, they were not created using a nationally recognised or comparable data standard. We changed to a new system/methodology in 2022 (see below) so previous annual figures are not directly comparable.'}
              source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Vaisala Road-AI switch</h3>
              <QuoteCard
                quote="Since 2022, we have been using the Vaisala Road-AI system to identify and monitor the condition of our Unclassified roads. A complete network survey was completed in 3 years, and our aim is to survey 1/3 of this network (800km) on an annual basis."
                source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why this matters for Section 58</h3>
              <p className="text-amber-800 text-sm mb-3">
                Any given U-road may not have been condition-surveyed in the current year — only one-third
                of the 3,283km network is assessed annually. The council documents that pre-2022 U-road RED
                figures used a different methodology entirely.
              </p>
              <p className="text-amber-800 text-sm">
                For your claim: ask when your road was last surveyed, whether it sits in the 23.7% RED
                category, and whether safety inspections — separate from condition surveys — should have
                caught the defect before it damaged your vehicle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Following The Money
          </h2>
          <p className="text-surface-600 mb-8">
            Gloucestershire spends well beyond its DfT allocation — but 42% of 2024/25 maintenance was reactive
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£30.0m</td>
                  <td className="px-4 py-3 text-center">£42.9m</td>
                  <td className="px-4 py-3 text-center">£20.6m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£22.3m</td>
                  <td className="px-4 py-3 text-center">£30.0m</td>
                  <td className="px-4 py-3 text-center">£20.3m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£22.3m</td>
                  <td className="px-4 py-3 text-center">£39.4m</td>
                  <td className="px-4 py-3 text-center">£29.1m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£29.0m</td>
                  <td className="px-4 py-3 text-center">£42.9m</td>
                  <td className="px-4 py-3 text-center">£30.0m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">59%</td>
                  <td className="px-4 py-3 text-center">41%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£25.1m</td>
                  <td className="px-4 py-3 text-center font-bold">£45.7m</td>
                  <td className="px-4 py-3 text-center">£33.2m</td>
                  <td className="px-4 py-3 text-center">58%</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">42%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (target)</td>
                  <td className="px-4 py-3 text-center">£35.3m</td>
                  <td className="px-4 py-3 text-center font-bold">£46.3m</td>
                  <td className="px-4 py-3 text-center">£30.8m</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-emerald-600">£78.9m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total maintenance spend 2024/25</div>
              <div className="text-xs text-surface-500 mt-1">Capital £45.7m + revenue £33.2m</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-emerald-600">1.82×</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Capital spend vs DfT allocation</div>
              <div className="text-xs text-surface-500 mt-1">£45.7m spent against £25.1m allocated</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">0.83%</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Of asset value spent on maintenance</div>
              <div className="text-xs text-surface-500 mt-1">Against £9.47bn estimated GRC (2025)</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Preventative vs Reactive — The Honest Split</h3>
            <p className="text-brand-200 mb-4">
              In 2024/25, Gloucestershire classed <strong className="text-white">58% of maintenance as preventative</strong> and{' '}
              <strong className="text-amber-400">42% as reactive</strong> — including pothole filling and emergency repairs.
              Just over £7m (23%) of the revenue budget goes to routine maintenance including pothole repairs;
              a further £6.4m (20%) to winter maintenance and drainage.
            </p>
            <p className="text-brand-200 mb-4">
              The council resurfaced or surface-dressed <strong className="text-white">121 miles of road in 2024/25</strong>,
              with 576 miles treated over five years. For 2025/26, over 100 miles are programmed.
            </p>
            <p className="text-white font-medium">
              GREEN spend does not eliminate potholes — it funds a network that still produced 62,131 safety
              defects last year. Nearly half of maintenance spend is chasing problems after they appear.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole volumes */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            200,996 Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The reactive repair volume on a GREEN-rated network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total safety defects</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Defects that were potholes</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">31,314</td>
                  <td className="px-4 py-3 text-center">38,517</td>
                  <td className="px-4 py-3 text-center">81%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">38,575</td>
                  <td className="px-4 py-3 text-center">46,950</td>
                  <td className="px-4 py-3 text-center">82%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">34,978</td>
                  <td className="px-4 py-3 text-center">42,334</td>
                  <td className="px-4 py-3 text-center">83%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">48,148</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">57,195</td>
                  <td className="px-4 py-3 text-center">84%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold">47,981</td>
                  <td className="px-4 py-3 text-center font-bold">62,131</td>
                  <td className="px-4 py-3 text-center">77%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-bold">Five-year total</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">200,996</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">247,127</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Find & Fix — preventative patching</h3>
              <p className="text-surface-600 text-sm mb-3">
                Gloucestershire's Find & Fix gangs treat defects before they become safety-category potholes:
              </p>
              <ul className="text-surface-600 text-sm space-y-1">
                <li>• 2023/24: 27,921 defects treated (part year)</li>
                <li>• 2024/25: 31,358 defects treated (full year)</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                Even with proactive gangs, recorded safety defects rose 61% from 2020/21 to 2024/25
                (38,517 → 62,131).
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 pace — already 8% ahead</h3>
              <QuoteCard
                quote="At the end of May 2025 we had fixed 11,378 safety defects in the current financial year which is 8% higher than in the same period in 2024/25."
                source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <QuoteCard
            quote="Safety Inspections are designed to identify all defects likely to cause danger, or serious inconvenience to users of the network or the wider community. The risk of danger is assessed on site and the defect identified with an appropriate priority response."
            source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* AMBER best practice + admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AMBER Scorecard And What The Council Admits
          </h2>
          <p className="text-surface-600 mb-8">
            Gloucestershire's one non-GREEN DfT mark — and the gaps its own words reveal
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Best Practice: AMBER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Gloucestershire publishes a TAMP updated April 2025, benchmarks against 15 statistical
                neighbours, tracks 12 public KPIs quarterly, and runs FixMyStreet integrated with CONFIRM.
                Yet the DfT still rated best practice AMBER — the scorecard measuring documented asset
                management processes, not road condition headlines.
              </p>
              <p className="text-amber-800 text-sm">
                Section 58 defences depend on proving a reasonable maintenance system. AMBER best practice
                is the official signal that Gloucestershire's evidenced processes have room for improvement
                — even while condition and spend score GREEN.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Public satisfaction vs survey data</h3>
              <QuoteCard
                quote="Condition surveys may not align with public perception of road conditions. For example in the 2024 NHT Public Satisfaction Survey, public satisfaction with the condition of highways in Gloucestershire saw a slight decline, dropping by 2 percentage points compared to the 2023 survey."
                source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Since 2011 there has been no national requirement for English highway authorities to survey and report on the condition of their Unclassified Road networks."
              source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="It is recognised that Climate Change has impacted much of our road network, particularly drainage, infrastructure and geotechnical assets."
              source="Gloucestershire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-xl p-6 border border-surface-200 mt-6">
            <h3 className="font-semibold text-brand-900 mb-3">FixMyStreet — 60% of reports now online</h3>
            <p className="text-surface-600 text-sm">
              Since launching FixMyStreet in November 2023, over 60% of highways issues are reported online
              each month — up from 38% before launch. Reports integrate with CONFIRM and subscribers can
              track progress. For your claim: prior FixMyStreet or council reports of the same defect location
              are powerful evidence of actual notice, which can defeat a Section 58 defence regardless of
              network-level GREEN ratings.
            </p>
          </div>
        </div>
      </section>

      {/* Section 58 analysis */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 58 On A GREEN Council
          </h2>
          <p className="text-surface-600 mb-8">
            How Gloucestershire will defend — and where the transparency data helps you
          </p>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">The Legal Framework</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41</strong> of the Highways Act 1980, Gloucestershire
              County Council must maintain highways maintainable at public expense. Under{' '}
              <strong className="text-white">Section 58</strong>, the council can defend a claim by proving it
              took such care as was reasonable to ensure the relevant part of the highway was not dangerous.
            </p>
            <p className="text-brand-200 mb-4">
              DfT GREEN ratings are network-level scorecards. They do not decide your case. The court asks:
              was this specific defect dangerous? Did the council know or ought to have known? Did it respond
              within its published inspection intervals?
            </p>
            <ul className="text-brand-300 space-y-2">
              <li>• On an A-road: expect a strong defence — 86% green-condition, surveyed annually both directions</li>
              <li>• On a B/C road: moderate — 29% in RED or amber, but regular SCANNER surveys</li>
              <li>• On a U-road: your strongest structural argument — 23.7% RED, only 800km surveyed per year, methodology caveats documented by the council itself</li>
            </ul>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition — classified roads above national average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital spend 1.8× DfT allocation in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> TAMP, 12 public KPIs, benchmarking against 15 neighbours
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Find & Fix gangs and 121 miles resurfaced in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> FixMyStreet integration — documented reporting trail
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-prepared Section 58 bundle. Generic negligence arguments will not work.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — documented process gaps at DfT level
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 23.7% of U-roads in RED — nearly doubled since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only one-third of U-roads condition-surveyed each year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 200,996 pothole repairs in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 42% reactive maintenance spend — chasing failures, not preventing them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Climate change admission on drainage and geotechnical assets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council's own note that U-road RED data is not nationally comparable
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mt-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a GREEN-rated county council, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior FixMyStreet or council reports of the same pothole — proof of actual notice</li>
              <li>• Photos showing depth, width and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the partial survey cycle and 23.7% RED rate are structural arguments</li>
              <li>• Whether the defect met Gloucestershire's safety inspection intervention criteria</li>
              <li>• Timeline — how long the defect existed vs the council's published response intervals</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior reports searched, photo evidence assessed, and
              Gloucestershire's own transparency data cited where it helps you — without pretending GREEN
              means invincible.
            </p>
          </div>
        </div>
      </section>

      {/* Forward plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Is Coming in 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            Forward programme from the council's own plans section
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Resurfacing programme</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 101+ miles programmed for resurfacing or surface dressing in 2025/26</li>
                <li>• Additional footway and structure funding in the capital programme</li>
                <li>• PAS 2161 methodology for classified roads from 2026/27 — five-band scoring replaces three-band RED/amber/green</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Innovation pipeline</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Battle of the Potholes trials — thermal repair, spray injection, mobile batched material</li>
                <li>• Expanded AI for road condition surveys and customer responses</li>
                <li>• Graphene surfacing trials to extend road life</li>
                <li>• Heatmaps to target defect hot spots</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Gloucestershire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated council demands a precise, evidence-led claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-cycle argument</li>
                <li>• No FixMyStreet prior-report search</li>
                <li>• Ignore AMBER best-practice scorecard</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 23.7% U-road RED condition documented</li>
                <li>✅ 200,996 repairs in five years cited</li>
                <li>✅ AMBER best-practice gap argued honestly</li>
                <li>✅ Prior FixMyStreet reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Gloucestershire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=gloucestershire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Gloucestershire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Gloucestershire's GREEN rating mean I cannot claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. GREEN means the council performs above average on network-level DfT scorecards — not that
                every road is defect-free. Section 58 under the Highways Act 1980 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired. Gloucestershire's own
                data shows 62,131 safety defects recorded in 2024/25 and 23.7% of unclassified roads in RED
                condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Best Practice AMBER if everything else is GREEN?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT scores condition, spend and best practice separately before combining them into an
                overall rating. Gloucestershire is GREEN on condition and spend but AMBER on best practice —
                the one scorecard that measures documented processes, asset management evidence and data
                submission. In a dispute, that is the official assessment that the council's systems have
                documented room for improvement, even while the network headline looks strong.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 3,283km — 56% of Gloucestershire's 5,868km network. Only 33% of
                U-roads are surveyed annually (800km per year on a three-year cycle). RED-condition U-roads
                have risen from 12% in 2020 to 23.7% in 2024 under the council's Vaisala Road-AI system —
                roughly 778km of residential and rural routes. The council itself notes these RED figures
                "were not created using a nationally recognised or comparable data standard" and that pre-2022
                figures are not directly comparable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council spending more than its DfT allocation weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It strengthens their Section 58 defence on aggregate — Gloucestershire spent £45.7m capital
                against £25.1m DfT allocation in 2024/25. But spend scorecards measure budgets, not your
                specific defect. The council still recorded 47,981 pothole repairs and 62,131 safety defects
                in 2024/25, with 42% of maintenance spend classed as reactive. A well-funded council can
                still miss the pothole that damaged your car.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 41 of the Highways Act 1980 apply here?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 41 imposes a duty on Gloucestershire County Council, as highway authority, to maintain
                adopted highways. Section 58 provides a defence if the council can prove it took such care as
                was reasonable to secure that the part of the highway in question was not dangerous. Your claim
                succeeds when you show the defect was dangerous, caused your damage, and the council either
                knew or ought to have known about it through its inspection regime — regardless of GREEN network
                ratings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.gloucestershire.gov.uk/plans-policies-procedures-and-manuals/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Gloucestershire County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
